import * as React from "react";

// A sideways-scrolling strip with its scrollbar hidden says nothing about what is off screen: the mail
// domain's fifth tab, "Advanced", sat past the right edge at 390px with nothing to show it was there
// (measured 2026-11-02). The edge that has more fades out — a mask, so it works on any background —
// and the active item (found by `activeSelector`) is scrolled into view when it changes.
export function useScrollHint<T extends HTMLElement>(activeSelector: string): {
  ref: React.RefObject<T | null>;
  style: React.CSSProperties | undefined;
} {
  const ref = React.useRef<T>(null);
  const [more, setMore] = React.useState({ left: false, right: false });
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const left = el.scrollLeft > 2;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
    };
    const showActive = () => {
      const a = el.querySelector<HTMLElement>(activeSelector);
      if (a && (a.offsetLeft < el.scrollLeft || a.offsetLeft + a.offsetWidth > el.scrollLeft + el.clientWidth)) {
        el.scrollTo({ left: Math.max(0, a.offsetLeft - 16), behavior: "smooth" });
      }
    };
    measure();
    showActive();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(() => {
      measure();
      showActive();
    });
    mo.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-state", "aria-current"] });
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [activeSelector]);
  const fade = 28;
  const style =
    more.left || more.right
      ? (() => {
          const mask = `linear-gradient(to right, ${more.left ? "transparent" : "black"} 0, black ${more.left ? fade : 0}px, black calc(100% - ${more.right ? fade : 0}px), ${more.right ? "transparent" : "black"} 100%)`;
          return { maskImage: mask, WebkitMaskImage: mask };
        })()
      : undefined;
  return { ref, style };
}
